import type { SupabaseClient } from "@supabase/supabase-js";

// Checkpoint setup data layer (CONSOLE-SETUP-CHECKPOINT-EDITOR). Reads: the hosted
// management_checkpoint_setup_list view (3867b67/1ee8868) - it intentionally hides token
// values/hashes, scan payloads and coordinates, guard/user/device ids - plus the hosted
// management_checkpoint_material_status view (20260802200000) for counts-only material state
// on pins the route-rooted views cannot see - plus ONE sanctioned base-table read: the
// PRD-001I placement reader below, because pins that are not on a route (walk drafts,
// promoted-but-unrouted pins, removed-from-route pins) are structurally absent from the
// route-rooted view. That reader is SELECT-only, RLS-scoped, and never selects
// placed_by_user_id; splitPlacementQueues partitions it client-side against the routed ids
// the view already provides. Writes: the hosted RPCs, schedule.manage gated server-side,
// archive-over-delete.
//
// THE ROUND-TRIP RULE: update_checkpoint takes the FULL field set - partial updates do not
// exist in the contract. checkpointDraftFromRow builds the complete payload from a view row
// so an editor that only changed core fields still carries every advanced field VERBATIM.

export interface CheckpointSetupRow {
  clientName: string | null;
  siteId: string;
  siteName: string | null;
  routeId: string;
  routeName: string | null;
  routeActive: boolean;
  routeCheckpointId: string;
  sortOrder: number;
  isRequired: boolean;
  expectedOffsetSeconds: number | null;
  windowStartsSeconds: number | null;
  windowEndsSeconds: number | null;
  checkpointId: string;
  checkpointName: string;
  checkpointActive: boolean;
  locationNotes: string | null;
  expectedLat: number | null;
  expectedLng: number | null;
  verificationStatus: string;
  statusReason: string | null;
  preferredMethod: string;
  fallbackMethods: string[];
  requiredEvidence: string[];
  geofenceRadiusMeters: number | null;
  geofenceMinAccuracyMeters: number | null;
  geofenceMinDwellSeconds: number | null;
  /** PRD-001Y Y3 AC-001Y.9 — optional L1 asset zone. Null when unlinked OR the zone was cleared. */
  siteAssetId: string | null;
  siteAssetName: string | null;
  /**
   * PRD-001I placement provenance (20260801120000). Older remotes without the columns degrade
   * to confirmed/console with null capture details; the console must render, not crash.
   * Routed rows read 'confirmed' in practice: provisional pins are refused on routes, so walk
   * drafts arrive through fetchPlacementCheckpoints below, never through this view.
   */
  placementState: string;
  placementSource: string;
  placementCapturedAccuracyMeters: number | null;
  placedAt: string | null;
}

/**
 * Shared row cap for every read in this module.
 *
 * Exported because a FULL setup list is not just "a long list": it means route membership on
 * this surface is incomplete. `fetchCheckpointSetupList` orders by sort_order, so the rows that
 * fall off are systematically the tail stops of every route, and a checkpoint that really is on
 * a live route can then be absent from the routed set entirely. Anything that tells an operator
 * what a checkpoint's routes ARE has to know when it cannot answer.
 */
export const CHECKPOINT_SETUP_LIST_LIMIT = 500;

const LIST_LIMIT = CHECKPOINT_SETUP_LIST_LIMIT;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

/** Every route-attached checkpoint with full setup context, route-then-order sorted. Throws safe. */
export async function fetchCheckpointSetupList(
  client: SupabaseClient,
  accountId: string,
): Promise<CheckpointSetupRow[]> {
  const { data, error } = await client
    .from("management_checkpoint_setup_list")
    .select("*")
    .eq("account_id", accountId)
    .order("sort_order", { ascending: true })
    .limit(LIST_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load checkpoint setup.");
  }
  const rows: CheckpointSetupRow[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const routeCheckpointId = asString(raw.route_checkpoint_id);
    const checkpointId = asString(raw.checkpoint_id);
    const routeId = asString(raw.route_id);
    const siteId = asString(raw.site_id);
    if (routeCheckpointId === null || checkpointId === null || routeId === null || siteId === null) {
      continue;
    }
    rows.push({
      clientName: asString(raw.client_name),
      siteId,
      siteName: asString(raw.site_name),
      routeId,
      routeName: asString(raw.route_name),
      routeActive: raw.route_active === true,
      routeCheckpointId,
      sortOrder: asNumber(raw.sort_order) ?? 0,
      isRequired: raw.is_required === true,
      expectedOffsetSeconds: asNumber(raw.expected_offset_seconds),
      windowStartsSeconds: asNumber(raw.window_starts_seconds),
      windowEndsSeconds: asNumber(raw.window_ends_seconds),
      checkpointId,
      checkpointName: asString(raw.checkpoint_name) ?? "Checkpoint",
      checkpointActive: raw.checkpoint_active === true,
      locationNotes: asString(raw.location_notes),
      expectedLat: asNumber(raw.expected_lat),
      expectedLng: asNumber(raw.expected_lng),
      verificationStatus: asString(raw.verification_status) ?? "active",
      statusReason: asString(raw.status_reason),
      preferredMethod: asString(raw.preferred_verification_method) ?? "qr",
      fallbackMethods: asStringArray(raw.allowed_fallback_methods),
      requiredEvidence: asStringArray(raw.required_evidence),
      geofenceRadiusMeters: asNumber(raw.geofence_radius_meters),
      geofenceMinAccuracyMeters: asNumber(raw.geofence_min_accuracy_meters),
      geofenceMinDwellSeconds: asNumber(raw.geofence_min_dwell_seconds),
      siteAssetId: asString(raw.site_asset_id),
      siteAssetName: asString(raw.site_asset_name),
      placementState: asString(raw.placement_state) ?? "confirmed",
      placementSource: asString(raw.placement_source) ?? "console",
      placementCapturedAccuracyMeters: asNumber(raw.placement_captured_accuracy_meters),
      placedAt: asString(raw.placed_at),
    });
  }
  return rows;
}

/**
 * Honest provenance sentence for editor surfaces. Dates render as the ISO day (locale free);
 * the console never sees or shows the placer's user id (the view excludes it by contract).
 */
export function placementProvenanceLabel(row: {
  placementSource: string;
  placementCapturedAccuracyMeters: number | null;
  placedAt: string | null;
}): string {
  const day = row.placedAt !== null && row.placedAt.length >= 10 ? row.placedAt.slice(0, 10) : null;
  if (row.placementSource === "field_setup_walk") {
    const accuracy =
      row.placementCapturedAccuracyMeters !== null
        ? ` (GPS accuracy ${row.placementCapturedAccuracyMeters} m)`
        : "";
    return `Field placed${day !== null ? ` on ${day}` : ""}${accuracy}`;
  }
  if (row.placementSource === "field_guard_proposal") {
    const accuracy =
      row.placementCapturedAccuracyMeters !== null
        ? ` (GPS accuracy ${row.placementCapturedAccuracyMeters} m)`
        : "";
    return `Guard proposal converted${day !== null ? ` on ${day}` : ""}${accuracy}`;
  }
  return `Console placed${day !== null ? ` on ${day}` : ""}`;
}

/**
 * PRD-001I placement row: an active checkpoint read directly from the RLS-scoped checkpoints
 * table. Pins without a route (walk drafts, promoted-but-unrouted pins, removed-from-route
 * pins) are structurally absent from management_checkpoint_setup_list (that view is
 * route-rooted), so this is the ONLY console read path for them, matching the mobile Setup
 * posture. The reader does not know route membership; splitPlacementQueues subtracts the
 * routed ids the setup view provides.
 */
export interface PlacementCheckpointRow {
  checkpointId: string;
  siteId: string;
  checkpointName: string;
  locationNotes: string | null;
  expectedLat: number | null;
  expectedLng: number | null;
  /** Null means this lightweight placement read could not identify the preferred method. */
  preferredMethod: string | null;
  /** 'provisional' | 'confirmed'; older remotes without the column degrade to confirmed. */
  placementState: string;
  /** Field setup, guard proposal, or console provenance; older remotes degrade to console. */
  placementSource: string;
  /**
   * 'active' | 'maintenance' | 'disabled'. Orthogonal to the `active` boolean this reader
   * filters on: archive_checkpoint sets both, but update_checkpoint can put an ACTIVE
   * checkpoint into maintenance. PRD-001Y Wave C paints unrouted and draft pins on the
   * Property map, so without this column a maintenance pin would render as healthy.
   */
  verificationStatus: string;
  placementCapturedAccuracyMeters: number | null;
  placedAt: string | null;
}

const PLACEMENT_COLUMNS =
  "id, site_id, name, location_notes, expected_lat, expected_lng, preferred_verification_method, placement_state, placement_source, verification_status, placement_captured_accuracy_meters, placed_at";

/**
 * Every ACTIVE checkpoint's placement identity, newest placement first (rows without
 * placed_at sort last and are the first to fall off the shared list limit). Returns [] on
 * any failure so a read problem can never block the routed editor around it.
 */
export async function fetchPlacementCheckpoints(
  client: SupabaseClient,
  accountId: string,
): Promise<PlacementCheckpointRow[]> {
  try {
    const { data, error } = await client
      .from("checkpoints")
      .select(PLACEMENT_COLUMNS)
      .eq("account_id", accountId)
      .eq("active", true)
      .order("placed_at", { ascending: false, nullsFirst: false })
      .limit(LIST_LIMIT);
    if (error !== null || !Array.isArray(data)) {
      return [];
    }
    const rows: PlacementCheckpointRow[] = [];
    for (const raw of data as Array<Record<string, unknown>>) {
      const checkpointId = asString(raw.id);
      const siteId = asString(raw.site_id);
      if (checkpointId === null || siteId === null) {
        continue;
      }
      rows.push({
        checkpointId,
        siteId,
        checkpointName: asString(raw.name) ?? "Checkpoint",
        locationNotes: asString(raw.location_notes),
        expectedLat: asNumber(raw.expected_lat),
        expectedLng: asNumber(raw.expected_lng),
        preferredMethod: asString(raw.preferred_verification_method),
        placementState: asString(raw.placement_state) ?? "confirmed",
        placementSource: asString(raw.placement_source) ?? "console",
        verificationStatus: asString(raw.verification_status) ?? "active",
        placementCapturedAccuracyMeters: asNumber(raw.placement_captured_accuracy_meters),
        placedAt: asString(raw.placed_at),
      });
    }
    return rows;
  } catch {
    return [];
  }
}

/**
 * Partition placement rows into the two Setup queues. Walk drafts are provisional pins
 * (unrouted by DB contract - add_checkpoint_to_route refuses them); unrouted confirmed pins
 * are the desk-mint targets (PRD-001I follow-up 1b). Both queues exclude routed ids as a
 * belt-and-suspenders guard so a routed pin can never grow a second mint surface here -
 * routed material stays on Patrols.
 */
export function splitPlacementQueues(
  rows: readonly PlacementCheckpointRow[],
  routedCheckpointIds: ReadonlySet<string>,
): { walkDrafts: PlacementCheckpointRow[]; unroutedConfirmed: PlacementCheckpointRow[] } {
  const walkDrafts: PlacementCheckpointRow[] = [];
  const unroutedConfirmed: PlacementCheckpointRow[] = [];
  for (const row of rows) {
    if (routedCheckpointIds.has(row.checkpointId)) {
      continue;
    }
    if (row.placementState === "provisional") {
      walkDrafts.push(row);
    } else {
      unroutedConfirmed.push(row);
    }
  }
  return { walkDrafts, unroutedConfirmed };
}

/**
 * PRD-001I Wave A2 material status. One row per checkpoint from
 * management_checkpoint_material_status (20260802200000) - counts ONLY. The route-rooted
 * rollout view cannot answer this question for a pin with no route, which is why Setup used
 * to frame an already-minted field pin as a first mint. The view exposes no token id, hash,
 * body, or human code by contract, and this mapper reads nothing beyond the three counts, so
 * a secret cannot reach a list payload even if the view were widened by accident.
 */
export interface CheckpointMaterialStatusRow {
  checkpointId: string;
  activeQrTokens: number;
  activeNfcTokens: number;
  activeCheckpointMaterial: number;
}

const MATERIAL_STATUS_COLUMNS =
  "checkpoint_id, active_qr_token_count, active_nfc_token_count, active_checkpoint_material_count";

function asCount(value: unknown): number {
  const parsed = asNumber(value);
  return parsed !== null && parsed > 0 ? Math.trunc(parsed) : 0;
}

/**
 * Active material counts keyed by checkpoint id. Returns an EMPTY map on any failure - the
 * console auto-deploys from main while migrations are applied by hand afterwards, so this read
 * must degrade to "material state unknown" (both mint affordances, honest confirm copy) rather
 * than break Setup or, worse, claim a minted pin has nothing on file.
 */
export async function fetchCheckpointMaterialStatus(
  client: SupabaseClient,
  accountId: string,
): Promise<Map<string, CheckpointMaterialStatusRow>> {
  const byCheckpointId = new Map<string, CheckpointMaterialStatusRow>();
  try {
    const { data, error } = await client
      .from("management_checkpoint_material_status")
      .select(MATERIAL_STATUS_COLUMNS)
      .eq("account_id", accountId)
      // Deterministic truncation. Both sibling readers order before limiting; without this an
      // account past LIST_LIMIT would drop an arbitrary, run-to-run different set of pins. The
      // dropped ones read as "unknown", which is the safe direction, but an operator seeing a
      // badge appear and vanish across reloads would rightly stop trusting the badge.
      .order("checkpoint_id", { ascending: true })
      .limit(LIST_LIMIT);
    if (error !== null || !Array.isArray(data)) {
      return byCheckpointId;
    }
    for (const raw of data as Array<Record<string, unknown>>) {
      const checkpointId = asString(raw.checkpoint_id);
      if (checkpointId === null) {
        continue;
      }
      byCheckpointId.set(checkpointId, {
        checkpointId,
        activeQrTokens: asCount(raw.active_qr_token_count),
        activeNfcTokens: asCount(raw.active_nfc_token_count),
        activeCheckpointMaterial: asCount(raw.active_checkpoint_material_count),
      });
    }
    return byCheckpointId;
  } catch {
    return byCheckpointId;
  }
}

/** The full checkpoint field set both create and update take. */
export interface CheckpointDraft {
  name: string;
  locationNotes: string | null;
  expectedLat: number | null;
  expectedLng: number | null;
  verificationStatus: string;
  statusReason: string | null;
  preferredMethod: string;
  fallbackMethods: string[];
  requiredEvidence: string[];
  geofenceRadiusMeters: number | null;
  geofenceMinAccuracyMeters: number | null;
  geofenceMinDwellSeconds: number | null;
  /** Asset zone to link, or null to clear it. Must belong to the same account AND site. */
  siteAssetId: string | null;
}

/** Route-attachment fields shared by add and update. */
export interface RouteCheckpointDraft {
  sortOrder: number;
  isRequired: boolean;
  expectedOffsetSeconds: number | null;
  windowStartsSeconds: number | null;
  windowEndsSeconds: number | null;
}

/**
 * Build the FULL update payload from a view row, overriding only the edited core fields.
 * This is what makes partial-feeling edits safe against the full-field contract.
 */
export function checkpointDraftFromRow(
  row: CheckpointSetupRow,
  edits: Partial<
    Pick<
      CheckpointDraft,
      | "name"
      | "locationNotes"
      | "expectedLat"
      | "expectedLng"
      | "preferredMethod"
      | "verificationStatus"
      | "statusReason"
      | "geofenceRadiusMeters"
      | "geofenceMinAccuracyMeters"
      | "geofenceMinDwellSeconds"
      | "siteAssetId"
    >
  > = {},
): CheckpointDraft & { siteId: string } {
  return {
    siteId: row.siteId,
    name: edits.name ?? row.checkpointName,
    locationNotes: edits.locationNotes !== undefined ? edits.locationNotes : row.locationNotes,
    expectedLat: edits.expectedLat !== undefined ? edits.expectedLat : row.expectedLat,
    expectedLng: edits.expectedLng !== undefined ? edits.expectedLng : row.expectedLng,
    verificationStatus: edits.verificationStatus ?? row.verificationStatus,
    statusReason: edits.statusReason !== undefined ? edits.statusReason : row.statusReason,
    preferredMethod: edits.preferredMethod ?? row.preferredMethod,
    // Fallback methods + required evidence still round-trip verbatim (no editor yet).
    fallbackMethods: [...row.fallbackMethods],
    requiredEvidence: [...row.requiredEvidence],
    // Geofence values are now editable; absent edits preserve the row's values.
    geofenceRadiusMeters:
      edits.geofenceRadiusMeters !== undefined ? edits.geofenceRadiusMeters : row.geofenceRadiusMeters,
    geofenceMinAccuracyMeters:
      edits.geofenceMinAccuracyMeters !== undefined ? edits.geofenceMinAccuracyMeters : row.geofenceMinAccuracyMeters,
    geofenceMinDwellSeconds:
      edits.geofenceMinDwellSeconds !== undefined ? edits.geofenceMinDwellSeconds : row.geofenceMinDwellSeconds,
    // The view only reports ACTIVE zones, so a checkpoint whose zone was cleared round-trips as
    // "None" and the stale link is dropped on the next save rather than failing validation forever.
    siteAssetId: edits.siteAssetId !== undefined ? edits.siteAssetId : row.siteAssetId,
  };
}

export const SAFE_CHECKPOINT_WRITE_FAILURE = "Couldn't save that checkpoint change - please try again.";

// Contract-stable, value-free sentences from the hosted checkpoint RPCs (20260611151224).
const KNOWN_CHECKPOINT_WRITE_FAILURES: ReadonlySet<string> = new Set([
  "Caller is not authorized to manage schedules for this account.",
  "Checkpoint name is required.",
  "Site was not found for this account.",
  "Route was not found for this account.",
  "Checkpoint and route must belong to the same site.",
  "Site asset was not found for this site.",
  "Route checkpoint was not found for this account.",
  "Archived routes cannot be edited.",
  "Account and route checkpoint ids are required.",
  "Account, route, and checkpoint ids are required.",
  // PRD-001I placement contract sentences (20260801120000).
  "Checkpoint was not found for this account.",
  "Authentication is required to promote checkpoint placements.",
  "Checkpoint promote caller mismatch.",
  "Account id is required to promote checkpoint placements.",
  "Checkpoint id is required to promote checkpoint placements.",
  "Checkpoint placement is already confirmed.",
  "Provisional checkpoints must be promoted before they join a patrol route.",
  // PRD-001 checkpoint Setup first-class host lane (20260812110000), verbatim. The permission
  // sentence moved from "manage schedules" to "manage checkpoint setup" across every checkpoint
  // RPC, and name uniqueness is now scoped to ACTIVE rows, which is what the collision sentence
  // now says. The legacy schedules sentence is kept above because the same allowlist still serves
  // route RPCs on remotes where this migration has not been applied yet.
  "Caller is not authorized to manage checkpoint setup for this account.",
  "An active checkpoint with this name already exists at this site.",
  "Authentication is required to archive checkpoints.",
  "Checkpoint archive caller mismatch.",
  "Account id is required to archive checkpoints.",
  "Checkpoint id is required to archive checkpoints.",
  "Authentication is required to restore checkpoints.",
  "Checkpoint restore caller mismatch.",
  "Account id is required to restore checkpoints.",
  "Site id is required to restore checkpoints.",
  "Checkpoint id is required to restore checkpoints.",
  "Checkpoint was not found at this site for this account.",
  "Checkpoint is already active.",
  "Restore outcome must be live or book_not_patrol_ready.",
  "Archived checkpoints must be restored before promotion.",
  "Archived checkpoints must be restored before they join a patrol route.",
]);

export type CheckpointWriteOutcome =
  | { ok: true; checkpointId: string | null; routeCheckpointId: string | null }
  /**
   * `code` is the raw PostgREST/Postgres code, carried for callers that must tell "this remote
   * does not have that RPC signature yet" apart from "the host said no". It is never rendered:
   * `reason` is the only operator-facing string.
   */
  | { ok: false; reason: string; code?: string };

// PostgREST's "no function with this argument list" code. Seen only in the window between a
// console deploy and the matching migration apply; see ASSET_LINK_DEPLOY_FALLBACK below.
const RPC_SIGNATURE_MISSING = "PGRST202";

async function runCheckpointWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<CheckpointWriteOutcome> {
  try {
    let { data, error } = await client.rpc(fn, params);
    // ASSET_LINK_DEPLOY_FALLBACK: the console auto-deploys from main but migrations are applied by
    // hand afterwards, so for a few minutes p_site_asset_id can reach a checkpoint RPC that does
    // not accept it yet. Retry once without it: the asset link is not saved, but creating and
    // editing checkpoints keeps working. Safe to delete once 20260731150000 is applied everywhere.
    if (error !== null && error.code === RPC_SIGNATURE_MISSING && "p_site_asset_id" in params) {
      const legacyParams: Record<string, unknown> = { ...params };
      delete legacyParams.p_site_asset_id;
      ({ data, error } = await client.rpc(fn, legacyParams));
    }
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_CHECKPOINT_WRITE_FAILURES.has(message) ? message : SAFE_CHECKPOINT_WRITE_FAILURE,
        ...(typeof error.code === "string" ? { code: error.code } : {}),
      };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    return {
      ok: true,
      checkpointId: typeof row?.checkpoint_id === "string" ? row.checkpoint_id : null,
      routeCheckpointId: typeof row?.route_checkpoint_id === "string" ? row.route_checkpoint_id : null,
    };
  } catch {
    return { ok: false, reason: SAFE_CHECKPOINT_WRITE_FAILURE };
  }
}

function routeCheckpointParams(draft: RouteCheckpointDraft): Record<string, unknown> {
  return {
    p_sort_order: draft.sortOrder,
    p_is_required: draft.isRequired,
    p_expected_offset_seconds: draft.expectedOffsetSeconds,
    p_window_starts_seconds: draft.windowStartsSeconds,
    p_window_ends_seconds: draft.windowEndsSeconds,
  };
}

function coerceGeofenceDwellSeconds(value: number | null): number | null {
  if (value === null) {
    return null;
  }
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.round(value);
}

function checkpointParams(draft: CheckpointDraft): Record<string, unknown> {
  return {
    p_name: draft.name,
    p_location_notes: draft.locationNotes,
    p_expected_lat: draft.expectedLat,
    p_expected_lng: draft.expectedLng,
    p_verification_status: draft.verificationStatus,
    p_status_reason: draft.statusReason,
    p_preferred_verification_method: draft.preferredMethod,
    p_allowed_fallback_methods: draft.fallbackMethods,
    p_required_evidence: draft.requiredEvidence,
    // Radius/accuracy: omit zero (DB CHECK requires null or > 0). Dwell: integer for the column.
    p_geofence_radius_meters:
      draft.geofenceRadiusMeters !== null && draft.geofenceRadiusMeters > 0
        ? draft.geofenceRadiusMeters
        : null,
    p_geofence_min_accuracy_meters:
      draft.geofenceMinAccuracyMeters !== null && draft.geofenceMinAccuracyMeters > 0
        ? draft.geofenceMinAccuracyMeters
        : null,
    p_geofence_min_dwell_seconds: coerceGeofenceDwellSeconds(draft.geofenceMinDwellSeconds),
    p_site_asset_id: draft.siteAssetId,
  };
}

export async function createCheckpoint(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  draft: CheckpointDraft,
): Promise<CheckpointWriteOutcome> {
  return runCheckpointWrite(client, "create_checkpoint", {
    p_account_id: accountId,
    p_site_id: siteId,
    ...checkpointParams(draft),
  });
}

export async function updateCheckpoint(
  client: SupabaseClient,
  accountId: string,
  checkpointId: string,
  draft: CheckpointDraft & { siteId?: string },
): Promise<CheckpointWriteOutcome> {
  return runCheckpointWrite(client, "update_checkpoint", {
    p_account_id: accountId,
    p_checkpoint_id: checkpointId,
    p_site_id: (draft as { siteId?: string }).siteId ?? null,
    ...checkpointParams(draft),
  });
}

/**
 * PRD-001 checkpoint Setup first-class (F8, locks 7 and 10). Retire is soft: disabled everywhere,
 * history kept forever, name freed, restorable. `removeFromRoutes` is the operator's explicit opt
 * in and defaults to false at every call site, because a route that suddenly has one fewer stop
 * rewrites the past - last week's completed walks would start looking like they covered a route
 * they did not.
 *
 * The opt in is sent as a third parameter. On a remote that still carries the two-parameter
 * signature the call is retried without it and `removedFromRoutes` comes back false, so the
 * surface can say the retire landed but the route cleanup did not. Archiving while silently
 * dropping the requested cleanup would be exactly the half-success this lane exists to avoid.
 */
export type ArchiveCheckpointOutcome =
  | { ok: true; checkpointId: string | null; routeCheckpointId: string | null; removedFromRoutes: boolean }
  | { ok: false; reason: string };

export async function archiveCheckpoint(
  client: SupabaseClient,
  accountId: string,
  checkpointId: string,
  removeFromRoutes = false,
): Promise<ArchiveCheckpointOutcome> {
  const withOptIn = await runCheckpointWrite(client, "archive_checkpoint", {
    p_account_id: accountId,
    p_checkpoint_id: checkpointId,
    p_remove_from_routes: removeFromRoutes,
  });
  if (withOptIn.ok) {
    return { ...withOptIn, removedFromRoutes: removeFromRoutes };
  }
  // Only a missing signature earns the retry. Anything else - a refusal, a dead network - must
  // surface as itself, or a transient failure would quietly become a retire without the cleanup.
  if (withOptIn.code !== RPC_SIGNATURE_MISSING) {
    return withOptIn;
  }
  const legacy = await runCheckpointWrite(client, "archive_checkpoint", {
    p_account_id: accountId,
    p_checkpoint_id: checkpointId,
  });
  return legacy.ok ? { ...legacy, removedFromRoutes: false } : legacy;
}

/**
 * WIRE values for `restore_checkpoint(p_outcome)`, verbatim from
 * 20260812110000_checkpoint_setup_first_class_host.sql. The product says "in the book, not
 * patrol-ready yet"; the contract says `book_not_patrol_ready`. Mirrors the mobile type exactly.
 */
export type CheckpointRestoreOutcome = "live" | "book_not_patrol_ready";

/**
 * PRD-001 checkpoint Setup first-class (F8, lock 9). Restore is optional and offers two honest
 * outcomes: Live, or in the book and not patrol-ready yet. It never edits routes.
 *
 * HOST DEPENDENCY: `restore_checkpoint` lands in the host lane. Until the migration is applied
 * PostgREST answers with a missing-function error, which surfaces as the safe sentence rather than
 * as a pretend success.
 */
export async function restoreCheckpoint(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  checkpointId: string,
  outcome: CheckpointRestoreOutcome,
): Promise<CheckpointWriteOutcome> {
  return runCheckpointWrite(client, "restore_checkpoint", {
    p_account_id: accountId,
    p_site_id: siteId,
    p_checkpoint_id: checkpointId,
    p_outcome: outcome,
  });
}

/**
 * F8's Location column, in the same words the phone uses. Coordinates set in the office are marked
 * as not confirmed until somebody stands at the stop and confirms them: that is the flag the field
 * uses to decide what still needs a walk.
 */
export function checkpointLocationLabel(row: {
  checkpointActive: boolean;
  expectedLat: number | null;
  expectedLng: number | null;
  placementSource: string;
}): string {
  if (!row.checkpointActive) {
    return "Retired, history kept";
  }
  if (row.expectedLat === null || row.expectedLng === null) {
    return "No coordinates";
  }
  return row.placementSource === "field_setup_walk" || row.placementSource === "field_guard_proposal"
    ? "Confirmed on site"
    : "Set in the office, not confirmed";
}

/** Every route this checkpoint currently sits on, for the retire dialog's route impact. */
export function routesForCheckpoint(
  rows: readonly CheckpointSetupRow[],
  checkpointId: string,
): Array<{ routeId: string; routeName: string }> {
  const seen = new Map<string, string>();
  for (const row of rows) {
    if (row.checkpointId === checkpointId && !seen.has(row.routeId)) {
      seen.set(row.routeId, row.routeName ?? "Route");
    }
  }
  return [...seen.entries()].map(([routeId, routeName]) => ({ routeId, routeName }));
}

export async function addCheckpointToRoute(
  client: SupabaseClient,
  accountId: string,
  routeId: string,
  checkpointId: string,
  draft: RouteCheckpointDraft,
): Promise<CheckpointWriteOutcome> {
  return runCheckpointWrite(client, "add_checkpoint_to_route", {
    p_account_id: accountId,
    p_route_id: routeId,
    p_checkpoint_id: checkpointId,
    ...routeCheckpointParams(draft),
  });
}

export async function updateRouteCheckpoint(
  client: SupabaseClient,
  accountId: string,
  routeCheckpointId: string,
  draft: RouteCheckpointDraft,
): Promise<CheckpointWriteOutcome> {
  return runCheckpointWrite(client, "update_route_checkpoint", {
    p_account_id: accountId,
    p_route_checkpoint_id: routeCheckpointId,
    ...routeCheckpointParams(draft),
  });
}

export async function removeCheckpointFromRoute(
  client: SupabaseClient,
  accountId: string,
  routeCheckpointId: string,
): Promise<CheckpointWriteOutcome> {
  return runCheckpointWrite(client, "remove_checkpoint_from_route", {
    p_account_id: accountId,
    p_route_checkpoint_id: routeCheckpointId,
  });
}

/**
 * PRD-001I AC-001I.3.4 desk promote. Flips placement_state provisional -> confirmed through
 * the dedicated hosted RPC and NOTHING else: update_checkpoint is a full-field replace with no
 * placement axis, so faking promote through it is forbidden (see file header).
 */
export async function promoteCheckpointPlacement(
  client: SupabaseClient,
  accountId: string,
  checkpointId: string,
): Promise<CheckpointWriteOutcome> {
  return runCheckpointWrite(client, "promote_checkpoint_placement", {
    p_account_id: accountId,
    p_checkpoint_id: checkpointId,
  });
}
